import React from "react";
import {
	Accordion,
	AccordionButton,
	AccordionItem,
	AccordionPanel,
	Box,
	Button,
	Flex,
	IconButton,
	Image,
	ModalBody,
	ModalFooter,
	Tag,
	Text,
	Tooltip,
	useDisclosure,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { ChakraFAIcon } from "@/components/ui/Icon";
import { ClasswizeModal } from "@/components/ui/Modal";
import { Notice } from "@/components/Notice";
import { colours, qoriaColours } from "@/theme/colours";
import { fonts } from "@/theme/fonts";
import { useSignatureStore } from "@/stores/v2/signatureStore";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { isAppliedList, List } from "../types";
import { formatWebsiteTarget, isOwnAppliedList, isQuickBlockList, isRuleInert } from "../utils";
import { RuleRelationshipTags, RuleSourceById } from "../RuleRelationshipTags";
import { AccordionChevron } from "./AccordionChevron";

interface LibraryListItemProps {
	list: List;
	onEdit?: () => void;
	onDelete: () => void;
	onApply?: () => void;
	applyDisabledReason?: string;
	isApplied?: boolean;
	/** True while this list's apply request is in flight. */
	isApplying?: boolean;
	/** True while this list's remove-from-class request is in flight. */
	isRemoving?: boolean;
	/** True while any apply is in flight, so a second apply cannot start. */
	applyBusy?: boolean;
	/** True while any remove is in flight, so a second remove cannot start. */
	removeBusy?: boolean;
	showEdit?: boolean;
	/** False when the trash/remove control only un-applies the list from this class. */
	confirmDelete?: boolean;
	ruleSourceById?: RuleSourceById;
}

export const LibraryListItem = (props: LibraryListItemProps): JSX.Element => {
	const { t } = useTranslation();
	const {
		list,
		onEdit,
		onDelete,
		onApply,
		applyDisabledReason,
		isApplied = false,
		isApplying = false,
		isRemoving = false,
		applyBusy = false,
		removeBusy = false,
		showEdit = true,
		confirmDelete = true,
		ruleSourceById = {},
	} = props;
	const lookupName = useSignatureStore((state) => state.lookupName);
	const getFavicon = useSignatureStore((state) => state.getFavicon);
	const findSignature = useSignatureStore((state) => state.findSignature);
	const currentQoriaUserId = useUserSessionStore((state) => state.userinfo?.qoriaUserId);
	const title = isQuickBlockList(list) ? t("rulesLibrary.quickBlock.listTitle") : list.title;
	const deleteConfirm = useDisclosure();
	let ownerName = "";
	let ownerAria: string | undefined;
	let ownerTooltip: string | undefined;
	let appliedListHelper: string | undefined;
	if (isAppliedList(list)) {
		if (isOwnAppliedList(list, currentQoriaUserId)) {
			ownerName = t("rulesLibrary.listOwnerYou");
			ownerAria = t("rulesLibrary.listOwnerYouAria");
			ownerTooltip = t("rulesLibrary.activeRules.listOwnerYouTooltip");
			appliedListHelper = t("rulesLibrary.activeRules.appliedListOwnHelper");
		} else if (list.teacherName.trim() !== "") {
			ownerName = list.teacherName.trim();
			ownerAria = t("rulesLibrary.listOwnerAria", { name: ownerName });
			ownerTooltip = t("rulesLibrary.activeRules.listOwnerTooltip", { name: ownerName });
			appliedListHelper = t("rulesLibrary.activeRules.appliedListOtherOwnerHelper", { name: ownerName });
		}
	}
	const ruleTooltips = isAppliedList(list)
		? { block: t("rulesLibrary.activeRules.ruleBlockedTooltip"), allow: t("rulesLibrary.activeRules.ruleAllowedTooltip") }
		: { block: t("rulesLibrary.ruleBlockedTooltip"), allow: t("rulesLibrary.ruleAllowedTooltip") };

	function handleRemoveClick(): void {
		if (isRemoving || removeBusy) {
			return;
		}

		if (confirmDelete) {
			deleteConfirm.onOpen();
			return;
		}

		onDelete();
	}

	function handleConfirmDelete(): void {
		deleteConfirm.onClose();
		onDelete();
	}

	return (
		<>
			<Accordion allowToggle>
				<AccordionItem
					border="1px solid"
					borderColor={qoriaColours.brand.neutral[100]}
					rounded="xl"
					bg={qoriaColours.brand.primary[10]}
					mb={3}
				>
					<Flex align="center" gap={2} px={4} py={3}>
						<AccordionButton flex={1} p={0} width="auto" fontFamily={fonts.body} _hover={{ bg: "transparent" }}>
							<AccordionChevron />
							<Flex
								align="center"
								justify="center"
								boxSize={8}
								ml={2}
								rounded="xl"
								bg={qoriaColours.brand.neutral[10]}
								boxShadow="sm"
								flexShrink={0}
							>
								<ChakraFAIcon icon="fa-layer-group" variant="solid" color={qoriaColours.brand.primary[600]} boxSize={4} />
							</Flex>
							<Box ml={3} textAlign="left">
								<Flex align="center" gap={2} wrap="wrap">
									<Text fontFamily={fonts.body} fontWeight={500} color={colours.text.title}>
										{title}
									</Text>
									{ownerName !== "" && ownerAria !== undefined && (
										<Tooltip label={ownerTooltip}>
											<Tag bg={qoriaColours.brand.neutral[10]} color={colours.text.regular} fontWeight={500} gap={2} aria-label={ownerAria}>
												<ChakraFAIcon icon="fa-user" variant="solid" boxSize={3} />
												{ownerName}
											</Tag>
										</Tooltip>
									)}
									{list.rules.some(isRuleInert) && (
										<Box as="span" display="inline-flex" role="img" aria-label={t("rulesLibrary.activeRules.notInEffectAria")}>
											<ChakraFAIcon icon="fa-triangle-exclamation" variant="solid" boxSize={4} color={qoriaColours.brand.warning[300]} />
										</Box>
									)}
								</Flex>
								<Text fontSize="sm" fontWeight={400} color={colours.text.regular}>
									{t("rulesLibrary.ruleCount", { count: list.rules.length })}
								</Text>
							</Box>
						</AccordionButton>
						{onApply &&
							(isApplied ? (
								<Tooltip label={t("rulesLibrary.appliedListTooltip")}>
									<Button
										as="span"
										h="32px"
										p="0 16px"
										borderRadius="lg"
										flexShrink={0}
										leftIcon={<ChakraFAIcon icon="fa-circle-check" variant="solid" boxSize={3.5} />}
										bg={qoriaColours.brand.success[10]}
										color={qoriaColours.brand.success[500]}
										cursor="default"
										tabIndex={-1}
										role="status"
										aria-label={t("rulesLibrary.appliedListAria", { name: title })}
										_hover={{ bg: qoriaColours.brand.success[10] }}
										_active={{ bg: qoriaColours.brand.success[10] }}
									>
										{t("rulesLibrary.appliedList")}
									</Button>
								</Tooltip>
							) : (
								<Tooltip label={applyDisabledReason ?? t("rulesLibrary.applyListTooltip")} shouldWrapChildren>
									<Button
										variant="neutralOutline"
										h="32px"
										p="0 16px"
										onClick={onApply}
										isDisabled={applyDisabledReason !== undefined || applyBusy || isApplying}
										isLoading={isApplying}
										aria-label={t("rulesLibrary.applyListAria", { name: title })}
									>
										{t("rulesLibrary.applyList")}
									</Button>
								</Tooltip>
							))}
						{showEdit && onEdit && (
							<Tooltip label={t("rulesLibrary.editListTooltip")}>
								<IconButton
									variant="ghost"
									aria-label={t("rulesLibrary.editRuleAria", { name: title })}
									flexShrink={0}
									boxSize="32px"
									minW="32px"
									h="32px"
									p={0}
									onClick={onEdit}
									icon={<ChakraFAIcon icon="fa-pen-to-square" variant="regular" color={colours.text.regular} boxSize={4} />}
								/>
							</Tooltip>
						)}
						{confirmDelete ? (
							<Tooltip label={t("rulesLibrary.deleteListTooltip")}>
								<IconButton
									aria-label={t("rulesLibrary.deleteRuleAria", { name: title })}
									variant="ghost"
									flexShrink={0}
									boxSize="32px"
									minW="32px"
									h="32px"
									p={0}
									onClick={handleRemoveClick}
									icon={<ChakraFAIcon icon="fa-trash-can" variant="regular" color={qoriaColours.brand.danger[500]} boxSize={4} />}
								/>
							</Tooltip>
						) : (
							<Tooltip label={t("rulesLibrary.activeRules.removeListTooltip")}>
								<Button
									size="sm"
									variant="outline"
									px={3}
									py={4}
									onClick={handleRemoveClick}
									isDisabled={isRemoving || removeBusy}
									isLoading={isRemoving}
									aria-label={t("rulesLibrary.removeListAria", { name: title })}
								>
									{t("rulesLibrary.removeList")}
								</Button>
							</Tooltip>
						)}
					</Flex>
					<AccordionPanel px={4} pt={0} pb={4}>
						<Flex direction="column" gap={2}>
							{appliedListHelper !== undefined && <Notice status="info">{appliedListHelper}</Notice>}
							{list.rules.map((rule) => {
								const blocked = rule.action === "block";
								const isSignature = rule.type === "SIGNATURE";
								const isUrl = rule.type === "URL";
								const label = isSignature ? lookupName(rule.target) : formatWebsiteTarget(rule.target);
								const favicon = isSignature && findSignature(rule.target) ? getFavicon(rule.target) : undefined;
								// Greyed out while another active rule overrides or conflicts with this one.
								const isInert = isRuleInert(rule);
								const statusBackground = blocked ? qoriaColours.brand.danger[10] : qoriaColours.brand.success[10];
								const statusColour = blocked ? qoriaColours.brand.danger[500] : qoriaColours.brand.success[500];
								return (
									<Flex
										key={rule.id}
										align="center"
										justify="space-between"
										gap={3}
										bg={qoriaColours.brand.neutral[10]}
										rounded="lg"
										px={4}
										py={2}
									>
										<Flex align="center" gap={3} flex={1} minW={0}>
											<Flex
												align="center"
												justify="center"
												boxSize={8}
												rounded="lg"
												bg={isUrl && !isInert ? statusBackground : undefined}
												flexShrink={0}
											>
												{favicon ? (
													<Image src={favicon} alt="" boxSize={5} opacity={isInert ? 0.4 : 1} />
												) : (
													<ChakraFAIcon
														icon="fa-earth-americas"
														variant="solid"
														color={isInert ? colours.form.disabled.text : isUrl ? statusColour : colours.text.regular}
														boxSize={4}
													/>
												)}
											</Flex>
											<Text
												fontFamily={fonts.body}
												fontWeight={400}
												color={isInert ? colours.form.disabled.text : colours.text.title}
												isTruncated
												title={label}
											>
												{label}
											</Text>
										</Flex>
										<Flex align="center" gap={2} flexShrink={0}>
											<RuleRelationshipTags rules={[rule]} sourceByRuleId={ruleSourceById} />
											<Tooltip label={blocked ? ruleTooltips.block : ruleTooltips.allow}>
												<Tag
													bg={isInert ? qoriaColours.brand.neutral[50] : statusBackground}
													color={isInert ? colours.form.disabled.text : statusColour}
													border="1px solid"
													borderColor={isInert ? qoriaColours.brand.neutral[200] : "transparent"}
													fontWeight={500}
													gap={2}
													whiteSpace="nowrap"
												>
													<ChakraFAIcon icon={blocked ? "fa-ban" : "fa-circle-check"} variant="solid" boxSize={3} />
													{blocked ? t("rulesLibrary.ruleStatusBlocked") : t("rulesLibrary.ruleStatusAllowed")}
												</Tag>
											</Tooltip>
										</Flex>
									</Flex>
								);
							})}
						</Flex>
					</AccordionPanel>
				</AccordionItem>
			</Accordion>
			<ClasswizeModal
				isOpen={deleteConfirm.isOpen}
				onClose={deleteConfirm.onClose}
				title={t("rulesLibrary.deleteListTitle")}
				size="lg"
				dataTestId="rules-library-delete-list"
			>
				<ModalBody>
					<Text>{t("rulesLibrary.deleteListBody", { name: title })}</Text>
					<Text mt={3}>{t("rulesLibrary.deleteListOtherClasses")}</Text>
				</ModalBody>
				<ModalFooter gap={2}>
					<Button variant="ghost" onClick={deleteConfirm.onClose}>
						{t("rulesLibrary.deleteListCancel")}
					</Button>
					<Button variant="danger" onClick={handleConfirmDelete}>
						{t("rulesLibrary.deleteListConfirm")}
					</Button>
				</ModalFooter>
			</ClasswizeModal>
		</>
	);
};
